import styles from './HeaderBookmarks.module.css'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bookmark } from '../../../../modules/system'
import { useSystemStateHooks } from '../../../../modules/system'
import { useBookmarkActions } from '../../hooks/useBookmarkActions'
import { BOOKMARK_ICONS, BOOKMARK_LABELS } from '../../bookmarkLabels'
import { MAX_BOOKMARKS } from '../../types'

const GROUPS = [
    { label: 'Ventas', routes: ['ventas', 'presupuestos', 'acopios', 'listas-de-precios', 'clientes', 'cuentas-corrientes', 'cuentas-corrientes/deudas'] },
    { label: 'Compras', routes: ['compras', 'proveedores'] },
    { label: 'Productos', routes: ['productos', 'categorias', 'stock'] },
    { label: 'Logística', routes: ['envios', 'hojas-de-ruta', 'conductores', 'vehiculos', 'recepciones', 'retiros', 'preparacion-de-envios'] },
    { label: 'Administración', routes: ['empleados', 'salarios', 'pagos', 'pagos-pendientes', 'cajas', 'cheques', 'tarjetas', 'transacciones', 'gastos', 'tipos-de-gasto', 'proveedores-de-gasto', 'impuestos', 'medios-de-pago'] },
]

const MORE_BTN_W = 90

export const HeaderBookmarks = () => {
    const [editMode, setEditMode] = useState(false)
    const [addMode, setAddMode] = useState(false)
    const [moreOpen, setMoreOpen] = useState(false)
    const [moreStart, setMoreStart] = useState<number | null>(null)
    const [measureKey, setMeasureKey] = useState(0)
    const [displayOrder, setDisplayOrder] = useState<Bookmark[]>([])

    const containerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const dragState = useRef<{ fromIndex: number; order: Bookmark[] }>({ fromIndex: 0, order: [] })

    const { bookmarks, isMaxReached, isBookmarked, addBookmark, removeBookmark, reorderBookmarks } = useBookmarkActions()
    const { companyCode } = useSystemStateHooks()
    const navigate = useNavigate()

    useEffect(() => {
        if (!editMode && !addMode && !moreOpen) return
        const handleOutsideClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setEditMode(false)
                setAddMode(false)
                setMoreOpen(false)
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [editMode, addMode, moreOpen])

    const runMeasure = useCallback(() => {
        const list = listRef.current
        if (!list) { setMoreStart(null); return }
        const chips = Array.from(list.children) as HTMLElement[]
        if (chips.length === 0) { setMoreStart(null); return }
        const listRect = list.getBoundingClientRect()
        const lastChip = chips[chips.length - 1]
        if (lastChip.getBoundingClientRect().right <= listRect.right + 1) {
            setMoreStart(null)
            return
        }
        const boundary = listRect.right - MORE_BTN_W
        let cutoff = chips.length
        for (let i = 0; i < chips.length; i++) {
            if (chips[i].getBoundingClientRect().right > boundary) {
                cutoff = i
                break
            }
        }
        setMoreStart(Math.max(1, cutoff))
    }, [])

    // Sync displayOrder and trigger re-measurement whenever bookmarks change (add/remove/reorder)
    useLayoutEffect(() => {
        setDisplayOrder([...bookmarks])
        setMoreStart(null)
        setMeasureKey(k => k + 1)
    }, [bookmarks.length])

    useLayoutEffect(() => {
        if (measureKey === 0) return
        runMeasure()
    }, [measureKey, runMeasure])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return
        const observer = new ResizeObserver(() => {
            setMoreStart(null)
            setMeasureKey(k => k + 1)
        })
        observer.observe(container)
        return () => observer.disconnect()
    }, [])

    const toggleAddMode = () => { setAddMode(p => !p); setEditMode(false); setMoreOpen(false) }
    const toggleEditMode = () => { setEditMode(p => !p); setAddMode(false); setMoreOpen(false) }
    const toggleMore = () => { setMoreOpen(p => !p); setAddMode(false); setEditMode(false) }

    const handleDragStart = (index: number) => {
        dragState.current = { fromIndex: index, order: [...displayOrder] }
    }
    const handleDragEnter = (targetIndex: number) => {
        const { fromIndex, order } = dragState.current
        if (fromIndex === targetIndex) return
        const reordered = [...order]
        const [moved] = reordered.splice(fromIndex, 1)
        reordered.splice(targetIndex, 0, moved)
        dragState.current = { fromIndex: targetIndex, order: reordered }
        setDisplayOrder(reordered)
    }
    const handleDragEnd = () => { reorderBookmarks(dragState.current.order) }
    const handleDragOver = (e: React.DragEvent) => { e.preventDefault() }

    const handleChipClick = (routePath: string) => {
        if (editMode) return
        navigate(`/${companyCode}/${routePath}`)
    }

    const visibleBookmarks = moreStart !== null ? displayOrder.slice(0, moreStart) : displayOrder
    const overflowBookmarks = moreStart !== null ? displayOrder.slice(moreStart) : []

    const renderChip = (bookmark: Bookmark, index: number) => (
        <div
            key={bookmark.routePath}
            className={`${styles.chip} ${editMode ? styles.editChip : ''}`}
            draggable={editMode}
            onDragStart={() => handleDragStart(index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            {editMode && <i className={`bi bi-grip-vertical ${styles.dragHandle}`} />}
            <button type="button" className={styles.chipBtn} onClick={() => handleChipClick(bookmark.routePath)}>
                <i className={`bi bi-${BOOKMARK_ICONS[bookmark.routePath] || 'star-fill'} ${styles.chipStar}`} />
                <span className={styles.chipLabel}>{bookmark.label}</span>
            </button>
            {editMode && (
                <button type="button" className={styles.deleteBtn} onClick={() => removeBookmark(bookmark.routePath)} title="Quitar favorito">
                    <i className="bi bi-x" />
                </button>
            )}
        </div>
    )

    return (
        <div ref={containerRef} className={styles.container}>
            {/* Add + Edit buttons — absolutely positioned so they don't affect flex layout */}
            <div className={`${styles.controlsGroup} ${(addMode || editMode) ? styles.controlsGroupActive : ''}`}>
                <div className={styles.addWrapper}>
                    <button
                        type="button"
                        className={`${styles.addBtn} ${addMode ? styles.modeActive : ''}`}
                        onClick={toggleAddMode}
                        title="Agregar favorito"
                    >
                        <i className="bi bi-plus-lg" />
                    </button>
                    {addMode && (
                        <div className={styles.dropdown}>
                            {isMaxReached && (
                                <div className={styles.dropdownMax}>Límite de {MAX_BOOKMARKS} favoritos alcanzado</div>
                            )}
                            {GROUPS.map(group => (
                                <div key={group.label}>
                                    <div className={styles.dropdownGroupTitle}>{group.label}</div>
                                    {group.routes.map(routePath => {
                                        const label = BOOKMARK_LABELS[routePath]
                                        const bookmarked = isBookmarked(routePath)
                                        const disabled = isMaxReached && !bookmarked
                                        return (
                                            <button
                                                key={routePath}
                                                type="button"
                                                className={`${styles.dropdownItem} ${bookmarked ? styles.dropdownItemBookmarked : ''}`}
                                                disabled={disabled}
                                                onClick={() => bookmarked ? removeBookmark(routePath) : addBookmark(routePath)}
                                            >
                                                <i className={`bi bi-${BOOKMARK_ICONS[routePath] || 'star-fill'} ${styles.dropdownIcon}`} />
                                                <span>{label}</span>
                                                <i className={`bi bi-check2 ${styles.dropdownCheck}`} />
                                                <i className={`bi bi-trash3 ${styles.dropdownTrash}`} />
                                            </button>
                                        )
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {displayOrder.length > 0 && (
                    <button
                        type="button"
                        className={`${styles.editBtn} ${editMode ? styles.modeActive : ''}`}
                        onClick={toggleEditMode}
                        title={editMode ? 'Confirmar' : 'Editar favoritos'}
                    >
                        <i className={`bi bi-${editMode ? 'check-lg' : 'pencil'}`} />
                    </button>
                )}
            </div>

            {/* Chips list or empty hint */}
            {displayOrder.length === 0 ? (
                <span className={styles.emptyHint}>Favoritos</span>
            ) : (
                <div ref={listRef} className={styles.list}>
                    {visibleBookmarks.map((bookmark, index) => renderChip(bookmark, index))}
                </div>
            )}

            {/* "Más" overflow button */}
            {overflowBookmarks.length > 0 && (
                <div className={styles.moreWrapper}>
                    <button
                        type="button"
                        className={`${styles.moreBtn} ${moreOpen ? styles.modeActive : ''}`}
                        onClick={toggleMore}
                    >
                        <span>Más</span>
                        <i className={`bi bi-chevron-${moreOpen ? 'up' : 'down'}`} />
                    </button>
                    {moreOpen && (
                        <div className={styles.moreDropdown}>
                            {overflowBookmarks.map(bookmark => (
                                <button
                                    key={bookmark.routePath}
                                    type="button"
                                    className={styles.moreDropdownItem}
                                    onClick={() => { handleChipClick(bookmark.routePath); setMoreOpen(false) }}
                                >
                                    <i className={`bi bi-${BOOKMARK_ICONS[bookmark.routePath] || 'star-fill'} ${styles.chipStar}`} />
                                    <span>{bookmark.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
